import React, { useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormHelperText,
  Switch,
  Typography,
} from "@mui/material";
import {
  VOICE_BASE_PLAN,
  cascadeVoiceSelection,
  voicePlansFromAddons,
} from "../utils/voiceSkuCatalog";
import { dueMonthly, formatDueMonthly } from "../utils/retailCompDisplay";

/**
 * Voice modal — Base + optional Calendar / Text Chat / Recording (TASK-769).
 * Toggle OFF while Base is already subscribed → same cancel confirm as Active Plans REMOVE.
 */
export default function VoicePlanModal({
  open,
  onClose,
  onApply,
  addons = [],
  selectedAddonIds = [],
  setSelectedAddonIds,
  activeVoiceBaseSubscription = null,
  onCancelActiveBase,
  cancellingBase = false,
  retailComp = false,
}) {
  const skus = useMemo(() => voicePlansFromAddons(addons), [addons]);
  const base = skus.find((s) => s.name === VOICE_BASE_PLAN);
  const pendingCancel = activeVoiceBaseSubscription?.cancel_at_period_end === 1;
  const baseIsLive =
    Boolean(activeVoiceBaseSubscription?.id) && !pendingCancel;
  const baseOn = Boolean(
    base?.id && selectedAddonIds.includes(base.id) && !pendingCancel,
  );

  const voiceSubtotal = useMemo(() => {
    let t = 0;
    for (const s of skus) {
      if (!s.id || !selectedAddonIds.includes(s.id) || !s.billable) continue;
      t += dueMonthly(s.monthly_price, retailComp);
    }
    return t;
  }, [skus, selectedAddonIds, retailComp]);

  const toggleBase = (checked) => {
    if (!base?.id) return;

    // Already entitled: OFF means real cancel (same as Active Plans → REMOVE)
    if (!checked && baseIsLive) {
      onCancelActiveBase?.();
      return;
    }

    setSelectedAddonIds((prev) => {
      let next = checked
        ? prev.includes(base.id)
          ? prev
          : [...prev, base.id]
        : prev.filter((id) => id !== base.id);
      next = cascadeVoiceSelection(addons, prev, next);
      return next;
    });
  };

  const toggleExtra = (sku, checked) => {
    if (!sku?.id || !baseOn) return;
    setSelectedAddonIds((prev) => {
      if (checked) {
        return prev.includes(sku.id) ? prev : [...prev, sku.id];
      }
      return prev.filter((id) => id !== sku.id);
    });
  };

  const extras = skus.filter((s) => s.name !== VOICE_BASE_PLAN);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>AI Phone Assistant</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Answers when nobody picks up or the shop is closed. Configure the
          assistant on voice.autoshops.ai after you subscribe.
        </Typography>

        {base && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: "grey.50" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={baseOn}
                  onChange={(e) => toggleBase(e.target.checked)}
                  disabled={!base.id || cancellingBase || pendingCancel}
                />
              }
              label={
                <Box>
                  <Typography fontWeight="bold">
                    {base.display_name} — $
                    {formatDueMonthly(base.monthly_price, retailComp)}/mo
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {base.blurb || base.description}
                  </Typography>
                </Box>
              }
            />
            {baseIsLive && (
              <FormHelperText sx={{ ml: 0, mt: 1 }}>
                Turning Voice Base off cancels it (same as Remove under Active
                Plans). Calendar / Recording / Text Chat cancel with Base, and
                the Voice phone number is released.
              </FormHelperText>
            )}
            {pendingCancel && (
              <FormHelperText sx={{ ml: 0, mt: 1 }}>
                Voice Base is set to cancel at the end of the billing period.
                You can Reinstate from Active Plans before then.
              </FormHelperText>
            )}
            {!base.id && (
              <FormHelperText error>
                Voice Base plan is missing from the catalog.
              </FormHelperText>
            )}
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Optional extras (require Voice Base)
        </Typography>
        {extras.map((sku) => {
          const checked = Boolean(sku.id && selectedAddonIds.includes(sku.id));
          const disabled = !baseOn || !sku.id || cancellingBase;
          return (
            <Box key={sku.name} sx={{ mb: 1.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => toggleExtra(sku, e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight="medium">
                      {sku.display_name} — $
                      {formatDueMonthly(sku.monthly_price, retailComp)}/mo
                      {!sku.billable ? " (not billed yet)" : ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sku.blurb || sku.description}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          );
        })}

        <Divider sx={{ my: 2 }} />
        <Typography fontWeight="bold">
          Voice subtotal: ${voiceSubtotal.toFixed(2)}/mo
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Cancelling Voice Base also removes Voice extras.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={cancellingBase}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled={cancellingBase}
          onClick={() => {
            onApply?.();
            onClose();
          }}
        >
          Apply to cart
        </Button>
      </DialogActions>
    </Dialog>
  );
}
